/* eslint-disable sonarjs/cognitive-complexity */
import {
    faAt,
    faBadgeDollar,
    faBuilding,
    faCircleQuestion,
    faClone,
    faCommentDollar,
    faComments,
    faHouse,
    faImage,
    faMapLocationDot,
    faSignOut,
    faStar,
    faTableList,
} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    buildFavouritesAndDefaultNavigationMenu,
    FAVORITES_SECTION_KEY,
    findItemByPath,
    getFirstPagePath,
} from '@rdcomponents/navigation/favorite-routes/favorites-helper';
import {nameForAnalytics} from '@rdcomponents/navigation/navigation-type';
import {ItemType} from 'antd/es/menu/interface';
import {useCallback, useContext, useMemo} from 'react';
import {useLocation} from 'react-router-dom';

import {appRoute} from '../../../../app-route';
import {InitialQuestionnaire} from '../../../../page/auth/initial-questionnaire/initial-questionnaire';
import {ErrorPage404} from '../../../../page/error/page-404/page-404';
import {GoogleAccountSyncPage} from '../../../../page/main/account-sync/google/google-account-sync-page';
import {GoogleConfirmations} from '../../../../page/main/account-sync/google/steps/finish/confirmations/google-confirmations';
import {AccountsMatching} from '../../../../page/main/accounts-matching/accounts-matchings';
import {BulkEditCompanies} from '../../../../page/main/bulk-edit-companies/bulk-edit-companies';
import {CompanyActivityAndSummary} from '../../../../page/main/company-activity-and-summary/company-activity-and-summary';
import {CreateCompanySelectBrand} from '../../../../page/main/company-form/create-company-select-brand/create-company-select-brand';
import {CreateCompanyPageGuard} from '../../../../page/main/company-form/page-guard/create/page-guard';
import {EditCompanyPageGuard} from '../../../../page/main/company-form/page-guard/edit/page-guard';
import {Dashboard} from '../../../../page/main/dashboard/dashboard';
import {FakesBadge} from '../../../../page/main/duplicates-and-fakes/common/fakes-badge/fakes-badge';
import {FakesActivation} from '../../../../page/main/duplicates-and-fakes/fakes-activation';
import {FakesPage} from '../../../../page/main/duplicates-and-fakes/fakes-page';
import {FakesSettingsAndStatistics} from '../../../../page/main/duplicates-and-fakes/settings-and-statistics/fakes-settings-and-statistics';
import {TechnicalSupport} from '../../../../page/main/help/technical-support/technical-support';
import {KnowledgeArticlePage} from '../../../../page/main/knowledge/knowledge-article-page/knowledge-article-page';
import {KnowledgeHelpVideos} from '../../../../page/main/knowledge/knowledge-help-videos/knowledge-help-videos';
import {useKnowledgeVideos} from '../../../../page/main/knowledge/knowledge-help-videos/use-knowledge-videos';
import {KnowledgePage} from '../../../../page/main/knowledge/knowledge-page';
import {CompaniesExport} from '../../../../page/main/my-companies/feed/export/feed-export-page';
import {CompaniesImport} from '../../../../page/main/my-companies/feed/import/companies-import';
import {FeedManagement} from '../../../../page/main/my-companies/feed/management/feed-management';
import {CompaniesValidation} from '../../../../page/main/my-companies/feed/validation/companies-validation';
import {MyCompanies} from '../../../../page/main/my-companies/my-companies';
import {Onboarding} from '../../../../page/main/onboarding/onboarding';
import {PhotoToolActivation} from '../../../../page/main/photo-tool/activation/photo-tool-activation';
import {PhotoToolBadge} from '../../../../page/main/photo-tool/components/phototool-badge/phototool-badge';
import {PhotoToolIndex} from '../../../../page/main/photo-tool/photo-tool-index';
import {CreateProductGroupPage} from '../../../../page/main/price/pages/products-group-form/create-product-group-page';
import {EditProductGroupPage} from '../../../../page/main/price/pages/products-group-form/edit-product-group-page';
import {Products} from '../../../../page/main/price/products';
import {PricesActivation} from '../../../../page/main/price/products-activation';
import {QuestionsAndAnswers} from '../../../../page/main/questions-and-answers/questions-and-answers';
import {SourcesConfirmations} from '../../../../page/main/sources/confirmations/confirmations';
import {YandexConfirmation} from '../../../../page/main/sources/provider-confirmations/yandex-confirmation';
import {Sources} from '../../../../page/main/sources/sources';
import {StoreLocatorIndex} from '../../../../page/main/store-locator/store-locator-index';
import {Tariffs} from '../../../../page/main/tariffs-and-limits/tariffs';
import {WidgetIndex} from '../../../../page/main/widget/widget-index';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {DomainNameEnum} from '../../../../provider/domain-config/domain-config-type';
import {useDocumentationLinks} from '../../../../provider/help-links/help-links-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {OnboardingContext} from '../../../../provider/onboarding/onboarding-context';
import {usePersonality} from '../../../../provider/personality/use-personality';
import {UiContext} from '../../../../provider/ui/ui-context';
import {useUser} from '../../../../provider/user/user-hook';
import {useFakesStatuses} from '../../../../service/fakes/fakes-statuses';
import {useChatwootSsoLink} from '../../../../service/omnichannel/chatwoot/chatwoot-hooks';
import {useTariffs} from '../../../../service/tariffs/tariffs-user-tariffs';
import {useLogoutMutation} from '../../../../service/user/user-logout';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {HeaderButtonEnum, useVisibleHeaderButtons} from '../../../header/common/header-buttons/header-buttons-hook';
import {useDoubleGisReportMenuItem} from '../../../navigation/double-gis-report/double-gis-report-menu-item';
import {
    getFeatureOrLandingPage,
    getMenuItemNameForAnalytics,
    isEnabledForDomainAndDoubleGisWhiteLabel,
} from '../../../navigation/navigation-helper';
import {buildMenuItemsFromNavigationConfig, getAvailableItems, navigationItemsToRouting} from '../helpers/common';
import {NavigationItemConfigType, NavigationItemRoutingType} from '../routing-type';

import {getIsUserAvailable} from './app-navigation-helper';
import {useCompetitorsNavigation} from './use-competitors-navigation';
import {useOnlinePresenceNavigation} from './use-online-presence-navigation';
import {usePostManagementNavigation} from './use-post-management-navigation';
import {useReviewsAnalysisNavigation} from './use-reviews-analysis-navigation';
import {useReviewsAutoReplyNavigation} from './use-reviews-autoreply-navigation';
import {useReviewsGeneratorNavigation} from './use-reviews-generator-navigation';
import {useReviewsManagementNavigation} from './use-reviews-management-navigation';
import {useSettingsNavigation} from './use-settings-navigation';

// eslint-disable-next-line max-statements
export function useNavigationConfig(): {
    navigationConfig: Array<NavigationItemConfigType>;
    menuItems: {
        favouriteMenuItems: Array<NavigationItemConfigType>;
        defaultMenuItems: Array<NavigationItemConfigType>;
    };
} {
    const {isMobileNavigationOpen} = useContext(UiContext);
    const {showOnboarding} = useContext(OnboardingContext);
    const {isUserProfileShow, domainName, LogoBreadcrumbs, canCreateCompany, statusPageUrl} = useDomainConfig();
    const documentationLinks = useDocumentationLinks();

    const {user, isReputationManager, isTechnicalSupportUser, favorites} = useUser();
    const {licenses, availableFeatures, enabledFeatures} = useLicenses();
    const {data: fakesStatuses} = useFakesStatuses();
    const {data: tariffs} = useTariffs();
    const {isStaff, isDoubleGisTrusted} = user || {};
    const {search} = useLocation();
    const {isWgisSupportUser} = usePersonality();
    const {getLocalizedString} = useLocale();

    const reviewsManagementRoutes = useReviewsManagementNavigation();
    const reviewsAutoReplyRoutes = useReviewsAutoReplyNavigation();
    const reviewsAnalysisRoutes = useReviewsAnalysisNavigation();
    const competitorsRoutes = useCompetitorsNavigation();
    const reviewsGeneratorRoutes = useReviewsGeneratorNavigation();
    const onlinePresenceRoutes = useOnlinePresenceNavigation();
    const settingsRoutes = useSettingsNavigation();
    const postManagementRoutes = usePostManagementNavigation();

    const showDoubleGisReportMenuItem = useMemo(() => isStaff && isDoubleGisTrusted, [isDoubleGisTrusted, isStaff]);

    const {recommendationVideoLinks} = useKnowledgeVideos();

    const doubleGisReportMenuItem = useDoubleGisReportMenuItem({
        skip: !showDoubleGisReportMenuItem,
    }) as unknown as NavigationItemConfigType; // todo: change type in parent hook??
    const visibleHeaderButtons = useVisibleHeaderButtons();
    const {mutate: logout} = useLogoutMutation();

    const mobileLogoutButtonVisible = useMemo(
        () => isMobileNavigationOpen && isUserProfileShow && !visibleHeaderButtons.includes(HeaderButtonEnum.Profile),
        [isMobileNavigationOpen, isUserProfileShow, visibleHeaderButtons]
    );

    const {mutate: openChatwoot} = useChatwootSsoLink();

    // IMPORTANT! Order routes as in ui menu
    const routeConfig: Array<NavigationItemConfigType> = useMemo(
        // eslint-disable-next-line complexity
        () => [
            {
                isAvailable: showOnboarding,
                isOnlyRoute: true,
                path: appRoute.onboarding.path,
                exact: true,
                component: Onboarding,
            },
            {
                isAvailable:
                    availableFeatures[FeaturesEnum.dashboard] && enabledFeatures.includes(FeaturesEnum.dashboard),
                path: appRoute.dashboard.path,
                icon: LogoBreadcrumbs ? <LogoBreadcrumbs /> : <FontAwesomeIcon icon={faHouse} />,
                label: getLocalizedString('CATEGORY_NAME__DASHBOARD'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__DASHBOARD'),
                exact: false,
                component: Dashboard,
                search,
            },
            {
                isAvailable:
                    availableFeatures[FeaturesEnum.myCompanies] && enabledFeatures.includes(FeaturesEnum.myCompanies),
                path: appRoute.myCompanies.path,
                icon: <FontAwesomeIcon icon={faBuilding} />,
                label: getLocalizedString('CATEGORY_NAME__MY_COMPANIES'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__MY_COMPANIES'),
                component: MyCompanies,
                exact: true,
                search,
                children: [
                    {
                        isAvailable: canCreateCompany,
                        isOnlyRoute: true,
                        path: appRoute.createCompanySelectBrand.path,
                        exact: true,
                        component: CreateCompanySelectBrand,
                    },
                    {
                        isAvailable: canCreateCompany,
                        isOnlyRoute: true,
                        path: appRoute.createCompany.path,
                        exact: true,
                        component: CreateCompanyPageGuard,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.editCompany.path,
                        exact: true,
                        component: EditCompanyPageGuard,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.bulkEditCompanies.path,
                        exact: true,
                        component: BulkEditCompanies,
                    },
                    {
                        isAvailable: true,
                        isOnlyRoute: true,
                        path: appRoute.companyActivityAndSummary.path,
                        exact: true,
                        component: CompanyActivityAndSummary,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.companyValidate.path,
                        exact: true,
                        component: CompaniesValidation,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.companyImport.path,
                        exact: true,
                        component: CompaniesImport,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.companyFeedManagement.path,
                        exact: true,
                        component: FeedManagement,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.companyExport.path,
                        exact: true,
                        component: CompaniesExport,
                    },
                ],
            },
            {
                isAvailable: availableFeatures[FeaturesEnum.sources] && enabledFeatures.includes(FeaturesEnum.sources),
                path: appRoute.sources.path,
                icon: <FontAwesomeIcon icon={faTableList} />,
                label: getLocalizedString('CATEGORY_NAME__SOURCES'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__SOURCES'),
                children: [
                    {
                        isOnlyRoute: true,
                        path: appRoute.sourcesAccountSyncGoogle.path,
                        exact: true,
                        component: GoogleAccountSyncPage,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.sourcesAccountSyncGoogleConfirmations.path,
                        exact: true,
                        component: GoogleConfirmations,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.sourcesConfirmations.path,
                        exact: true,
                        component: SourcesConfirmations,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.sourcesYandexConfirmation.path,
                        exact: true,
                        component: YandexConfirmation,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.accountsMatching.path,
                        exact: true,
                        component: AccountsMatching,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.sources.path,
                        exact: true,
                        component: Sources,
                    },
                ],
            },
            {
                ...doubleGisReportMenuItem,
                key: doubleGisReportMenuItem?.key ?? 'double-gis-report',
                isAvailable: Boolean(showDoubleGisReportMenuItem),
                isCustom: true,
            },
            ...reviewsManagementRoutes,
            ...reviewsAutoReplyRoutes,
            ...reviewsAnalysisRoutes,
            ...competitorsRoutes,
            ...reviewsGeneratorRoutes,
            {
                isAvailable:
                    availableFeatures[FeaturesEnum.questionsAndAnswers] &&
                    enabledFeatures.includes(FeaturesEnum.questionsAndAnswers),
                isActivated: licenses[FeaturesEnum.questionsAndAnswers]?.isActive,
                path: appRoute.questionsAndAnswers.path,
                icon: <FontAwesomeIcon icon={faComments} />,
                label: getLocalizedString('CATEGORY_NAME__QUESTIONS_N_ANSWERS'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__QUESTIONS_N_ANSWERS'),
                exact: true,
                component: QuestionsAndAnswers,
            },
            {
                isAvailable:
                    availableFeatures[FeaturesEnum.photoTool] && enabledFeatures.includes(FeaturesEnum.photoTool),
                isActivated: licenses[FeaturesEnum.photoTool]?.isActive,
                path: appRoute.photoToolIndex.path,
                icon: <FontAwesomeIcon icon={faImage} />,
                labelComponent: <PhotoToolBadge />,
                label: getLocalizedString('CATEGORY_NAME__PHOTO_TOOL'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__PHOTO_TOOL'),
                exact: false,
                component: getFeatureOrLandingPage({
                    license: licenses[FeaturesEnum.photoTool],
                    activationComponent: PhotoToolActivation,
                    pageComponent: PhotoToolIndex,
                }),
            },
            ...onlinePresenceRoutes,
            {
                isAvailable: availableFeatures[FeaturesEnum.reviewsWidget],
                isActivated: licenses[FeaturesEnum.reviewsWidget]?.isActive,
                path: appRoute.reviewWidget.path,
                icon: <FontAwesomeIcon icon={faClone} />,
                label: getLocalizedString('CATEGORY_NAME__WIDGET'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__WIDGET'),
                exact: false,
                component: WidgetIndex,
            },
            {
                isAvailable:
                    isEnabledForDomainAndDoubleGisWhiteLabel({
                        isDomainFeatureEnabled: availableFeatures[FeaturesEnum.storeLocator] || false,
                        isFeatureEnabled: Boolean(licenses[FeaturesEnum.storeLocator]?.isActive),
                        domainName,
                    }) && enabledFeatures.includes(FeaturesEnum.storeLocator),
                isActivated: licenses[FeaturesEnum.storeLocator]?.isActive,
                path: appRoute.storeLocator.path,
                icon: <FontAwesomeIcon icon={faMapLocationDot} />,
                label: getLocalizedString('CATEGORY_NAME__STORE_LOCATOR'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__STORE_LOCATOR'),
                exact: false,
                component: StoreLocatorIndex,
            },
            {
                isAvailable: licenses[FeaturesEnum.chatwoot]?.isActive && availableFeatures[FeaturesEnum.chatwoot],
                icon: <FontAwesomeIcon icon={faAt} />,
                isCustom: true,
                label: getLocalizedString('CATEGORY_NAME__CHATWOOT'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__CHATWOOT'),
                onClick: () => openChatwoot(),
            },
            ...postManagementRoutes,
            {
                isAvailable:
                    availableFeatures[FeaturesEnum.duplicatesAndFakes] &&
                    getIsUserAvailable(isWgisSupportUser) &&
                    !isReputationManager &&
                    enabledFeatures.includes(FeaturesEnum.duplicatesAndFakes),
                isActivated: licenses[FeaturesEnum.duplicatesAndFakes]?.isActive,
                path: appRoute.duplicatesAndFakes.path,
                icon: <FontAwesomeIcon icon={faClone} />,
                labelComponent: <FakesBadge />,
                label: getLocalizedString('CATEGORY_NAME__DUBS_AND_FAKES'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__DUBS_AND_FAKES'),
                exact: true,
                component: licenses[FeaturesEnum.duplicatesAndFakes]?.isActive
                    ? domainName === DomainNameEnum.brandWizard && fakesStatuses?.isTrial
                        ? FakesActivation
                        : FakesPage
                    : FakesActivation,
                children: [
                    {
                        isOnlyRoute: true,
                        isActivated: licenses[FeaturesEnum.duplicatesAndFakes]?.isActive,
                        path: appRoute.fakesSettings.path,
                        exact: true,
                        component: licenses[FeaturesEnum.duplicatesAndFakes]?.isActive
                            ? domainName === DomainNameEnum.brandWizard && fakesStatuses?.isTrial
                                ? FakesActivation
                                : FakesSettingsAndStatistics
                            : FakesActivation,
                    },
                ],
            },
            {
                path: appRoute.products.path,
                icon: <FontAwesomeIcon icon={faCommentDollar} />,
                label: getLocalizedString('CATEGORY_NAME__PRODUCTS'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__PRODUCTS'),
                exact: true,
                component: licenses[FeaturesEnum.prices]?.isActive ? Products : PricesActivation,
                isAvailable:
                    getIsUserAvailable(isWgisSupportUser) &&
                    !isReputationManager &&
                    availableFeatures[FeaturesEnum.prices] &&
                    enabledFeatures.includes(FeaturesEnum.prices),
                isActivated: licenses[FeaturesEnum.prices]?.isActive,
                children: [
                    {
                        isOnlyRoute: true,
                        path: appRoute.createProductGroup.path,
                        exact: true,
                        component: CreateProductGroupPage,
                    },
                    {
                        isOnlyRoute: true,
                        path: appRoute.editProductsGroup.path,
                        exact: true,
                        component: EditProductGroupPage,
                    },
                ],
            },
            {
                path: appRoute.tariffsAndLimits.path,
                isAvailable:
                    tariffs &&
                    tariffs.results.length > 0 &&
                    !isReputationManager &&
                    availableFeatures[FeaturesEnum.tariffs] &&
                    enabledFeatures.includes(FeaturesEnum.tariffs),
                icon: <FontAwesomeIcon icon={faBadgeDollar} />,
                label: getLocalizedString('CATEGORY_NAME__TARIFFS'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__TARIFFS'),
                exact: true,
                component: Tariffs,
            },
            ...settingsRoutes,
            {
                isAvailable:
                    availableFeatures[FeaturesEnum.knowledge] && enabledFeatures.includes(FeaturesEnum.knowledge),
                exact: false,
                path: appRoute.help.path,
                icon: <FontAwesomeIcon icon={faCircleQuestion} />,
                label: getLocalizedString('CATEGORY_NAME__HELP'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__HELP'),
                deniedPath: [appRoute.help.path],
                redirectPath: appRoute.helpTechnicalSupport.path,
                children: [
                    {
                        isAvailable: availableFeatures[FeaturesEnum.knowledge] && isTechnicalSupportUser,
                        exact: true,
                        component: TechnicalSupport,
                        label: getLocalizedString('CATEGORY_NAME__HELP__TECHNICAL_SUPPORT'),
                        [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__HELP__TECHNICAL_SUPPORT'),
                        path: appRoute.helpTechnicalSupport.path,
                    },
                    {
                        isAvailable: availableFeatures[FeaturesEnum.knowledge] && isTechnicalSupportUser,
                        isOnlyRoute: true,
                        component: TechnicalSupport,
                        path: appRoute.helpTechnicalSupportTicket.path,
                    },
                    {
                        isAvailable: Boolean(documentationLinks.help),
                        exact: availableFeatures[FeaturesEnum.knowledge],
                        isCustom: !availableFeatures[FeaturesEnum.knowledge],
                        labelComponent: availableFeatures[FeaturesEnum.knowledge] ? (
                            <Locale stringKey="CATEGORY_NAME__KNOWLEDGE" />
                        ) : (
                            <a href={documentationLinks.help} rel="noreferrer" target="_blank">
                                <Locale stringKey="CATEGORY_NAME__KNOWLEDGE" />
                            </a>
                        ),
                        label: getLocalizedString('CATEGORY_NAME__KNOWLEDGE'),
                        [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__KNOWLEDGE'),
                        path: appRoute.knowledge.path,
                        component: KnowledgePage,
                    },
                    {
                        isAvailable: Boolean(documentationLinks.help) && availableFeatures[FeaturesEnum.knowledge],
                        isOnlyRoute: true,
                        path: appRoute.knowledgeArticle.path,
                        exact: true,
                        component: KnowledgeArticlePage,
                    },
                    {
                        isAvailable: Boolean(statusPageUrl) && domainName !== DomainNameEnum.brandWizard,
                        isCustom: true,
                        isOnlyRoute: false,
                        key: 'knowledge-system-status',
                        label: getLocalizedString('KNOWLEDGE__SYSTEM_STATUS'),
                        labelComponent: (
                            <a href={statusPageUrl} rel="noreferrer" target="_blank">
                                <Locale stringKey="KNOWLEDGE__SYSTEM_STATUS" />
                            </a>
                        ),
                        [nameForAnalytics]: getMenuItemNameForAnalytics('KNOWLEDGE__SYSTEM_STATUS'),
                    },
                    {
                        isAvailable: recommendationVideoLinks.length > 0 && domainName === DomainNameEnum.rocketData,
                        exact: true,
                        isCustom: false,
                        label: getLocalizedString('CATEGORY_NAME__HELP__VIDEOS'),
                        [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__HELP__VIDEOS'),
                        path: appRoute.knowledgeHelpVideos.path,
                        component: KnowledgeHelpVideos,
                    },
                ],
            },
            {
                isAvailable: domainName === DomainNameEnum.brandWizard && Boolean(statusPageUrl),
                isCustom: true,
                isOnlyRoute: false,
                key: 'bw-system-status',
                label: getLocalizedString('KNOWLEDGE__SYSTEM_STATUS'),
                labelComponent: (
                    <a href={statusPageUrl} rel="noreferrer" target="_blank">
                        <Locale stringKey="KNOWLEDGE__SYSTEM_STATUS" />
                    </a>
                ),
                [nameForAnalytics]: getMenuItemNameForAnalytics('KNOWLEDGE__SYSTEM_STATUS'),
            },
            {
                isAvailable: recommendationVideoLinks.length > 0 && domainName === DomainNameEnum.brandWizard,
                isOnlyRoute: false,
                path: appRoute.knowledgeHelpVideos.path,
                label: getLocalizedString('CATEGORY_NAME__HELP__VIDEOS'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__HELP__VIDEOS'),
                component: KnowledgeHelpVideos,
            },
            {
                isAvailable: mobileLogoutButtonVisible,
                isCustom: true,
                key: 'logout',
                icon: <FontAwesomeIcon icon={faSignOut} />,
                label: getLocalizedString('HEADER__USER__TEXT__LOGOUT'),
                [nameForAnalytics]: getMenuItemNameForAnalytics('HEADER__USER__TEXT__LOGOUT'),
                onClick: () => logout(),
            },
            {
                isOnlyRoute: true,
                path: appRoute.questionnaire.path,
                exact: true,
                component: InitialQuestionnaire,
            },
            // always should be last, for correct work
            {
                isOnlyRoute: true,
                path: '*',
                exact: false,
                component: ErrorPage404,
            },
        ],
        [
            LogoBreadcrumbs,
            availableFeatures,
            canCreateCompany,
            competitorsRoutes,
            documentationLinks.help,
            domainName,
            doubleGisReportMenuItem,
            enabledFeatures,
            fakesStatuses?.isTrial,
            getLocalizedString,
            isReputationManager,
            isTechnicalSupportUser,
            isWgisSupportUser,
            licenses,
            logout,
            mobileLogoutButtonVisible,
            onlinePresenceRoutes,
            openChatwoot,
            postManagementRoutes,
            recommendationVideoLinks.length,
            reviewsAnalysisRoutes,
            reviewsAutoReplyRoutes,
            reviewsGeneratorRoutes,
            reviewsManagementRoutes,
            search,
            settingsRoutes,
            showDoubleGisReportMenuItem,
            showOnboarding,
            statusPageUrl,
            tariffs,
        ]
    );

    const availableItems = getAvailableItems(routeConfig);

    const firstAvailableRouteConfig = availableItems.find(
        (item) => !item.isOnlyRoute || item.path === appRoute.onboarding.path
    );

    const redirectPathForRoot =
        favorites?.length > 0
            ? (() => {
                  const firstFavorite = favorites[0];

                  if (firstFavorite) {
                      const found = findItemByPath(availableItems, firstFavorite);

                      if (found) {
                          return getFirstPagePath(found.item);
                      }
                  }

                  return null;
              })()
            : null;

    return useMemo(() => {
        const rootRoute: NavigationItemConfigType = firstAvailableRouteConfig
            ? {
                  ...firstAvailableRouteConfig,
                  redirectPath:
                      redirectPathForRoot ?? firstAvailableRouteConfig.redirectPath ?? firstAvailableRouteConfig.path,
                  deniedPath: [
                      ...(firstAvailableRouteConfig.deniedPath || []),
                      appRoute.root.path,
                      appRoute.login.path,
                      appRoute.register.path,
                      appRoute.forgotPassword.path,
                  ],
              }
            : {};

        const updatedRouteConfig = [rootRoute, ...routeConfig.filter((route) => rootRoute?.path !== route.path)];

        const availableNavigationItems = getAvailableItems(updatedRouteConfig);

        const {favouriteMenuItems, defaultMenuItems} = buildFavouritesAndDefaultNavigationMenu(
            availableNavigationItems,
            favorites
        );

        return {
            navigationConfig: availableNavigationItems,
            menuItems: {
                favouriteMenuItems,
                defaultMenuItems,
            },
        };
    }, [favorites, firstAvailableRouteConfig, redirectPathForRoot, routeConfig]);
}

export function useMenuNavigation(): {
    visibleMenuItems: Array<ItemType>;
    allMenuItems: Array<ItemType>;
} {
    const {menuItems} = useNavigationConfig();
    const {favouriteMenuItems, defaultMenuItems} = menuItems;

    const {getLocalizedString} = useLocale();

    const getMenuItems = useCallback(
        (includeOnlyRoutes: boolean) => {
            return buildMenuItemsFromNavigationConfig(
                [
                    ...(favouriteMenuItems?.length > 0
                        ? [
                              {
                                  key: FAVORITES_SECTION_KEY,
                                  icon: <FontAwesomeIcon icon={faStar} />,
                                  label: getLocalizedString('CATEGORY_NAME__FAVORITES'),
                                  [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__FAVORITES'),
                                  children: favouriteMenuItems,
                              },
                          ]
                        : []),
                    ...defaultMenuItems,
                ],
                includeOnlyRoutes
            );
        },
        [defaultMenuItems, favouriteMenuItems, getLocalizedString]
    );

    return useMemo(() => {
        return {
            visibleMenuItems: getMenuItems(false),
            allMenuItems: getMenuItems(true),
        };
    }, [getMenuItems]);
}

export function useAppNavigation(): Array<NavigationItemRoutingType> {
    const {navigationConfig} = useNavigationConfig();

    return navigationItemsToRouting(navigationConfig);
}
